import { useCallback, useEffect, useState } from 'react';
import { useResizeDetector } from 'react-resize-detector';

/**
 * Wraps useResizeDetector with fullscreenchange/visibilitychange listeners
 * to force a size re-read when ResizeObserver misses updates (e.g., background tab).
 */
export const useContainerSize = () => {
  const {
    width: observedWidth,
    height: observedHeight,
    ref,
  } = useResizeDetector({
    handleWidth: true,
    handleHeight: true,
    refreshMode: 'debounce',
    refreshRate: 16,
  });

  const [correctedSize, setCorrectedSize] = useState<{
    width?: number;
    height?: number;
  } | null>(null);

  const measureElement = useCallback(() => {
    const element = ref.current;

    if (!element) {
      return;
    }

    const rect = element.getBoundingClientRect();

    setCorrectedSize({ width: rect.width, height: rect.height });
  }, [ref]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      measureElement();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        measureElement();
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [measureElement]);

  useEffect(() => {
    if (correctedSize) {
      setCorrectedSize(null);
    }
  }, [observedWidth, observedHeight]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    width: correctedSize?.width ?? observedWidth,
    height: correctedSize?.height ?? observedHeight,
    ref,
  };
};
